// The Document Centre's field vocabulary: the types, owners and linked-detail keys a template
// field can carry. Shared by the page and its field-list components, so the two can never drift
// apart on what a valid option is.
export const fieldTypes = [
  "TEXT",
  "DATE",
  "EMAIL",
  "PHONE",
  "MULTILINE",
  "CHECKBOX",
  "SIGNATURE",
  "PHOTO",
];
export const signerRoles = [
  "ADMIN_PRE_SEND",
  "CLIENT",
  "ADMIN_POST_SIGN",
  // Admin fills it before sending; left blank, the client must fill it before they can submit.
  "CLIENT_IF_NOT_ADMIN_FILLED",
  // Client fills it if they can; left blank, admin closes the gap after signing.
  "ADMIN_IF_NOT_CLIENT_FILLED",
];
export const AUTOFILL_OWNER = "AUTOFILLED";
export const CLIENT_IF_NONE_OWNER = "CLIENT_IF_NONE";
// A linked field is normally AUTOFILLED, but may also opt into a role that resolves differently:
// ADMIN_IF_NONE autofills yet lets admin fill any gap and never blocks a send (form 956's agent
// and client details), CLIENT_IF_NONE falls back to the client, and ADMIN_POST_SIGN resolves after
// the client signs (the agent signature and completion date).
export const LINKED_ONLY_OWNERS = [
  AUTOFILL_OWNER,
  CLIENT_IF_NONE_OWNER,
  "ADMIN_POST_SIGN",
  "ADMIN_IF_NONE",
];
export const fieldTypeByPrefillKey = {
  "client.email": "EMAIL",
  "client.number": "PHONE",
  "client.dob": "DATE",
  "client.passportExpiry": "DATE",
  "client.dateOfIssue": "DATE",
  "client.visaExpiry": "DATE",
  "client.address": "MULTILINE",
  "client.emergencyContactNumber": "PHONE",
  "client.emergencyEmail": "EMAIL",
  "mara.businessAddress": "MULTILINE",
  "mara.email": "EMAIL",
  "mara.mobile": "PHONE",
  "mara.phone": "PHONE",
  "mara.officePhoneNumber": "PHONE",
  "client.officePhoneNumber": "PHONE",
  "mara.signature": "SIGNATURE",
  "mara.titleMr": "CHECKBOX",
  "mara.titleMrs": "CHECKBOX",
  "mara.titleMiss": "CHECKBOX",
  "mara.titleMs": "CHECKBOX",
  "mara.isRegisteredAgent": "CHECKBOX",
  "mara.isLegalPractitioner": "CHECKBOX",
  "mara.isExemptPerson": "CHECKBOX",
  "system.today": "DATE",
};
export const fieldTypeForPrefillKey = (key) => fieldTypeByPrefillKey[key] || "TEXT";
export const withAutofillApplied = (field) =>
  field.prefillKey
    ? {
        ...field,
        fieldType: fieldTypeForPrefillKey(field.prefillKey),
        signerRole: LINKED_ONLY_OWNERS.includes(field.signerRole)
          ? field.signerRole
          : AUTOFILL_OWNER,
      }
    : field;
export const linkedDetailGroups = [
  {
    group: "Client",
    options: [
      { value: "client.name", label: "Name" },
      { value: "client.familyName", label: "Family name" },
      { value: "client.givenNames", label: "Given names" },
      { value: "client.email", label: "Email" },
      { value: "client.number", label: "Mobile number" },
      { value: "client.nationality", label: "Nationality" },
      { value: "client.dob", label: "Date of birth" },
      { value: "client.dobDdMmYyyy", label: "Date of birth (DD/MM/YYYY)" },
      { value: "client.passportNumber", label: "Passport number" },
      { value: "client.passportExpiry", label: "Passport expiry" },
      { value: "client.dateOfIssue", label: "Passport date of issue" },
      { value: "client.visaStatus", label: "Visa status" },
      { value: "client.visaExpiry", label: "Visa expiry" },
      { value: "client.usiNumber", label: "USI number" },
      { value: "client.address", label: "Address" },
      { value: "client.addressStreet", label: "Address: street (both lines)" },
      { value: "client.addressSuburb", label: "Address: suburb / state" },
      { value: "client.officePhoneCountryCode", label: "Office phone: country code" },
      { value: "client.officePhoneAreaCode", label: "Office phone: area code" },
      { value: "client.officePhoneNumber", label: "Office phone: number" },
      { value: "client.address2", label: "Address 2" },
      { value: "client.country", label: "Country" },
      { value: "client.state", label: "State" },
      { value: "client.city", label: "City" },
      { value: "client.zipCode", label: "Zip code" },
      { value: "client.emergencyContactName", label: "Emergency contact name" },
      { value: "client.emergencyContactNumber", label: "Emergency contact number" },
      { value: "client.emergencyEmail", label: "Emergency contact email" },
      { value: "client.emergencyRelationship", label: "Emergency contact relationship" },
    ],
  },
  {
    group: "Application (Visa)",
    options: [
      { value: "application.visa.serviceCharge", label: "Service charge" },
      { value: "application.visa.visaCharge", label: "Visa charge" },
      { value: "application.visa.extraCharges", label: "Extra charges" },
      { value: "application.visa.kotharCost", label: "Kothar cost" },
      { value: "application.visa.discount", label: "Discount" },
      { value: "application.visa.serviceChargeFinal", label: "Service charge (GST-inclusive)" },
      { value: "application.visa.visaChargeFinal", label: "Visa charge (GST-inclusive)" },
      { value: "application.visa.extraChargesFinal", label: "Extra charges (GST-inclusive)" },
      { value: "application.visa.kotharCostFinal", label: "Kothar cost (GST-inclusive)" },
      {
        value: "application.visa.professionalFeesExGst",
        label: "Professional fees (GST-exclusive)",
      },
      { value: "application.visa.professionalFeesGst", label: "GST on professional fees" },
      {
        value: "application.visa.professionalFeesInclGst",
        label: "Professional fees (GST-inclusive)",
      },
      {
        value: "application.visa.totalFeesFinal",
        label: "Total fees incl. disbursements (GST-inclusive)",
      },
      { value: "application.visa.amountReceived", label: "Amount received (verified payments)" },
      { value: "application.visa.receivedOn", label: "Latest verified payment date" },
      { value: "application.visa.depositStatus", label: "Deposit status" },
      { value: "application.visa.balanceOutstanding", label: "Remaining balance" },
      { value: "application.visa.balanceStatus", label: "Remaining balance status" },
      { value: "application.visa.finalPaymentDueDate", label: "Final payment due date" },
      { value: "application.visa.serviceChargeGstRate", label: "Service charge GST rate (%)" },
      { value: "application.visa.visaChargeGstRate", label: "Visa charge GST rate (%)" },
      { value: "application.visa.type", label: "Type" },
      { value: "application.visa.status", label: "Status" },
      { value: "application.visa.reference", label: "Reference" },
      { value: "application.visa.caseOfficer", label: "Case officer" },
      { value: "application.visa.visa", label: "Visa" },
    ],
  },
  {
    group: "MARA agent",
    options: [
      { value: "mara.consultantAgent", label: "Consultant agent" },
      { value: "mara.agentName", label: "Agent name" },
      { value: "mara.familyName", label: "Family name" },
      { value: "mara.givenNames", label: "Given names" },
      { value: "mara.title", label: "Title (Mr/Mrs/Miss/Ms)" },
      { value: "mara.titleOther", label: "Title (other)" },
      { value: "mara.titleMr", label: "Title box: Mr (ticks if matched)" },
      { value: "mara.titleMrs", label: "Title box: Mrs (ticks if matched)" },
      { value: "mara.titleMiss", label: "Title box: Miss (ticks if matched)" },
      { value: "mara.titleMs", label: "Title box: Ms (ticks if matched)" },
      { value: "mara.agentType", label: "Registered as" },
      { value: "mara.isRegisteredAgent", label: "Capacity box: registered migration agent" },
      { value: "mara.isLegalPractitioner", label: "Capacity box: legal practitioner" },
      { value: "mara.isExemptPerson", label: "Capacity box: exempt person" },
      { value: "mara.maraRegistrationNo", label: "MARA registration no. (MARN)" },
      { value: "mara.lpn", label: "Legal practitioner no. (LPN)" },
      { value: "mara.businessAddress", label: "Business address" },
      { value: "mara.addressStreet", label: "Address: street" },
      { value: "mara.addressSuburb", label: "Address: suburb / state" },
      { value: "mara.addressCountry", label: "Address: country" },
      { value: "mara.addressPostcode", label: "Address: postcode" },
      { value: "mara.correspondenceStreet", label: "Correspondence: street (or AS ABOVE)" },
      { value: "mara.correspondenceSuburb", label: "Correspondence: suburb / state" },
      { value: "mara.correspondenceCountry", label: "Correspondence: country" },
      { value: "mara.correspondencePostcode", label: "Correspondence: postcode" },
      { value: "mara.officePhoneCountryCode", label: "Office phone: country code" },
      { value: "mara.officePhoneAreaCode", label: "Office phone: area code" },
      { value: "mara.officePhoneNumber", label: "Office phone: number" },
      { value: "mara.email", label: "Email address" },
      { value: "mara.mobile", label: "Mobile no." },
      { value: "mara.phone", label: "Phone" },
      { value: "mara.signature", label: "Agent signature" },
    ],
  },
  {
    group: "System",
    options: [{ value: "system.today", label: "Today's date (resolved when signed)" }],
  },
];
